"use client";

import { useState } from "react";
import { Notice, SelectField } from "@/components/tool-kit/primitives";
import {
  alphaOptions,
  ExampleButton,
} from "@/components/tools/stats-tests/kit";
import {
  MatrixInput,
  matrixProblemText,
} from "@/components/tools/stats-tests-more/matrix-input";
import { RepeatedMeasuresAnovaResults } from "@/components/tools/stats-tests-more/repeated-measures-anova-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  conditionNames,
  parseMatrix,
} from "@/lib/tool-kit/stats-tests-more/matrix";
import { repeatedMeasuresAnova } from "@/lib/tool-kit/stats-tests-more/repeated-measures-anova";

/** English strings. A translated content file overrides these through `ui`. */
export const REPEATED_MEASURES_ANOVA_CALCULATOR_STRINGS = {
  dataLabel:
    "Scores — one row per subject, one column per condition (paste from a spreadsheet or separate with commas or spaces)",
  dataPlaceholder: "45, 50, 55, 60\n42, 42, 45, 53\n36, 41, 43, 55",
  namesLabel: "Condition names (optional, separated by commas)",
  namesPlaceholder: "Week 1, Week 2, Week 3, Week 4",
  conditionDefault: "Condition {n}",
  subjectsDetected: "one: subject|other: subjects",
  conditionsDetected: "one: condition|other: conditions",
  errorInvalid: "Line {line}: “{token}” is not a number.",
  errorRagged:
    "Line {line} has {got} values, but the first row has {expected}. Every subject needs a score in every condition.",
  alphaLabel: "Significance level (α)",
  correctionLabel: "Degrees-of-freedom correction for the main test",
  correctionAuto: "Automatic (based on Mauchly's test)",
  correctionNone: "None (sphericity assumed)",
  correctionGg: "Greenhouse–Geisser",
  correctionHf: "Huynh–Feldt",
  tryExample: "Try an example",
  exampleNames: "Week 1, Week 2, Week 3, Week 4",
  exampleData:
    "45, 50, 55, 60\n42, 42, 45, 53\n36, 41, 43, 55\n39, 35, 40, 43\n51, 55, 59, 67\n44, 49, 56, 61\n49, 52, 50, 64\n41, 44, 46, 58\n38, 40, 48, 47\n47, 52, 53, 65",
  results: "Results",
  fStatistic: "F statistic",
  dfLabel: "Degrees of freedom",
  pValue: "p-value",
  partialEta: "Partial η²",
  generalizedEta: "Generalized η²",
  subjects: "Subjects (n)",
  usedNone: "Test shown: sphericity assumed (no correction).",
  usedGg: "Test shown: Greenhouse–Geisser correction, ε = {eps}.",
  usedHf: "Test shown: Huynh–Feldt correction, ε = {eps}.",
  decisionReject:
    "Reject H₀ at α = {alpha}: p = {p} is below α — the condition means are not all equal.",
  decisionKeep:
    "Do not reject H₀ at α = {alpha}: p = {p} is not below α — no evidence that the condition means differ.",
  tableHeading: "ANOVA table (sphericity assumed)",
  source: "Source",
  conditions: "Conditions",
  subjectsRow: "Subjects",
  error: "Error (conditions × subjects)",
  total: "Total",
  ss: "SS",
  df: "df",
  ms: "MS",
  f: "F",
  p: "p",
  descriptivesHeading: "Condition descriptives",
  condition: "Condition",
  mean: "Mean",
  sd: "SD",
  sphericityHeading: "Sphericity",
  sphericityNone:
    "With two conditions there is only one set of differences, so sphericity holds automatically and no correction is needed.",
  mauchly: "Mauchly's test: W = {w}, χ²({df}) = {chi}, p = {p}.",
  mauchlyViolated:
    "Sphericity is rejected at α = {alpha}: the variances of the differences between conditions are unequal. Use a corrected test.",
  mauchlyHolds:
    "No significant departure from sphericity at α = {alpha}. Mauchly's test has low power in small samples, so a non-significant result does not prove sphericity — reporting a correction anyway is reasonable.",
  mauchlyUnavailable:
    "Mauchly's test is not available: the covariance matrix is singular, usually because there are fewer subjects than conditions.",
  epsilonLine:
    "Greenhouse–Geisser ε = {gg}; Huynh–Feldt ε = {hf} (capped at 1); lower bound = {lower}.",
  guideline:
    "A common rule (Girden, 1992): use Greenhouse–Geisser when ε is below .75 and Huynh–Feldt when it is above.",
  correctedHeading: "Corrected F tests",
  correction: "Correction",
  epsilon: "ε",
  df1: "df 1",
  df2: "df 2",
  assumed: "Sphericity assumed",
  posthocHeading: "Pairwise comparisons (paired t-tests)",
  posthocHint:
    "Each pair is a paired t-test on the {n} differences, which does not depend on sphericity. Bonferroni multiplies each p by the number of comparisons ({m}); Holm's step-down adjustment is never less powerful.",
  comparison: "Comparison",
  difference: "Mean difference",
  se: "SE",
  t: "t",
  pBonferroni: "p (Bonferroni)",
  pHolm: "p (Holm)",
  dz: "d_z",
  versus: "{a} − {b}",
  howCalculated: "How this was calculated",
  apaHeading: "APA-style result",
  apaTemplate: "*F*({df1}, {df2}) = {f}, *p* {p}, ηₚ² = {eta}",
  apaNoteNone:
    "Degrees of freedom are uncorrected (sphericity assumed). ηₚ² is partial eta squared; report the condition means and SDs with the test.",
  apaNoteGg:
    "Degrees of freedom are Greenhouse–Geisser corrected, so they are not whole numbers; say so in the text. ηₚ² is partial eta squared.",
  apaNoteHf:
    "Degrees of freedom are Huynh–Feldt corrected, so they are not whole numbers; say so in the text. ηₚ² is partial eta squared.",
  mauchlyApaHeading: "APA-style sphericity result",
  mauchlyApaTemplate: "Mauchly’s *W* = {w}, χ²({df}) = {chi}, *p* {p}",
  copyApa: "Copy APA result",
  copied: "Copied!",
  prompt:
    "Enter at least two subjects and at least two conditions to see the result.",
  errorVariance:
    "Every subject changes by exactly the same amount across conditions, so the error term is zero and F is undefined.",
} as const;

export type CorrectionChoice = "auto" | "none" | "gg" | "hf";

export default function RepeatedMeasuresAnovaCalculatorWidget() {
  const strings = useToolUi(REPEATED_MEASURES_ANOVA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [data, setData] = useState("");
  const [names, setNames] = useState("");
  const [alpha, setAlpha] = useState("0.05");
  const [correction, setCorrection] = useState<CorrectionChoice>("auto");

  const parsed = parseMatrix(data);
  const problem = matrixProblemText(parsed, strings, fmt);
  const outcome =
    problem === null && parsed.rows.length >= 2
      ? repeatedMeasuresAnova(parsed.rows)
      : null;
  const labels = conditionNames(names, parsed.header, parsed.width, (n) =>
    t(strings.conditionDefault, { n })
  );

  let notice: string | null = problem;
  if (notice === null && outcome && !outcome.ok) {
    notice =
      outcome.reason === "no-error-variance"
        ? strings.errorVariance
        : strings.prompt;
  } else if (notice === null && !outcome) {
    notice = strings.prompt;
  }

  return (
    <div className="space-y-6">
      <MatrixInput
        data={data}
        fmt={fmt}
        idPrefix="rm"
        locale={locale}
        names={names}
        onData={setData}
        onNames={setNames}
        parsed={parsed}
        text={strings}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-full max-w-xs">
          <SelectField
            id="rm-alpha"
            label={strings.alphaLabel}
            onChange={setAlpha}
            options={alphaOptions(fmt)}
            value={alpha}
          />
        </div>
        <div className="w-full max-w-xs">
          <SelectField
            id="rm-correction"
            label={strings.correctionLabel}
            onChange={setCorrection}
            options={[
              { value: "auto", label: strings.correctionAuto },
              { value: "none", label: strings.correctionNone },
              { value: "gg", label: strings.correctionGg },
              { value: "hf", label: strings.correctionHf },
            ]}
            value={correction}
          />
        </div>
        <ExampleButton
          label={strings.tryExample}
          onClick={() => {
            setData(strings.exampleData);
            setNames(strings.exampleNames);
          }}
        />
      </div>
      {outcome?.ok ? (
        <RepeatedMeasuresAnovaResults
          alpha={Number(alpha)}
          choice={correction}
          fmt={fmt}
          names={labels}
          r={outcome.result}
          strings={strings}
        />
      ) : (
        <Notice>{notice}</Notice>
      )}
    </div>
  );
}
